// El estado de cada modal es un objeto reactive que se lee antes del await y se
// escribe despues; la regla lo marca como carrera aunque cada modal tiene una
// sola accion en curso (el boton queda deshabilitado mientras corre).
/* eslint-disable require-atomic-updates */
import { computed, reactive, ref } from "vue";
import { useStore } from 'vuex';

// Nombres legibles de respaldo si GET actions falla (ids de hoy en la BD)
const ACCION_LABELS = {
    create: 'Crear',
    updated: 'Actualizar',
    read: 'Leer',
    delete: 'Eliminar',
    manage: 'Todos',
};

// Igual que Str::slug(nombre, '_') del backend, para avisar nombres repetidos:
// el login arma los permisos por slug y dos roles iguales suman permisos.
const slugDe = (texto) => String(texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// El CRM anterior guardaba la descripcion como HTML (Quill)
const textoPlano = (html) => {
    if (!html) return '';
    if (!/<[a-z][\s\S]*>/i.test(html)) return String(html);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').trim();
};

const listadoVacio = () => ({
    items: [], page: 1, lastPage: 1, total: 0, loading: false,
});

export function useRoles() {
    const store = useStore();
    const alertTable = ref({ state: false, message: '', color: 'success' });

    let alertTimer = null;
    const showAlert = (message, color = 'success') => {
        if (alertTimer) clearTimeout(alertTimer);
        alertTable.value = { state: true, message, color };
        alertTimer = setTimeout(() => { alertTable.value.state = false; }, 6000);
    };

    // Estos endpoints no validan y devuelven los errores como texto o como la
    // excepcion cruda; se muestra lo que haya y si no el mensaje por defecto.
    const mensajeError = (error, fallback) => {
        const payload = error.response?.data;
        if (typeof payload?.errorMessage === 'string' && payload.errorMessage) return payload.errorMessage;
        if (typeof payload?.data === 'string' && payload.data) return payload.data;
        if (typeof payload?.message === 'string' && payload.message) return payload.message;
        return fallback;
    };

    // --- Usuario ---------------------------------------------------------
    const usuario = computed(() => store.state.appUserdata.userdata || {});
    // El backend no comprueba el superadmin en el catalogo ni en is_main
    const esSuperAdmin = computed(() => usuario.value?.role?.slug === 'administrator');

    const tab = ref('roles');

    // --- Catalogo de acciones ---------------------------------------------
    const catalogo = computed(() => store.state.appRoles.catalogo);
    const acciones = computed(() => store.state.appRoles.acciones);
    const loadingCatalogo = ref(false);

    // En permissions-actions `title` trae el codigo; el nombre sale de GET actions
    const accionLabel = (id, codigo) => {
        const accion = acciones.value.find((a) => a.id === id);
        if (accion?.title) return accion.title;
        const code = codigo || accion?.action;
        return ACCION_LABELS[code] || code || `#${id}`;
    };

    const accionCodigo = (id, fallback) => acciones.value.find((a) => a.id === id)?.action || fallback;

    const cargarCatalogo = async () => {
        loadingCatalogo.value = true;
        try {
            await Promise.all([
                store.dispatch('appRoles/getCatalogoAction'),
                store.dispatch('appRoles/getAccionesAction').catch(() => []),
            ]);
        } catch (error) {
            showAlert(mensajeError(error, 'No se pudo cargar el catálogo de permisos'), 'danger');
        } finally {
            loadingCatalogo.value = false;
        }
    };

    const tituloModulo = (id, subject) => catalogo.value.find((m) => m.id === id)?.title || subject || `#${id}`;

    // --- Listado de roles ------------------------------------------------
    const roles = reactive(listadoVacio());
    const busqueda = ref('');
    // Todos los roles visibles (sin permisos), para avisar nombres repetidos
    const todosLosRoles = ref([]);

    const cargarRoles = async (page = roles.page) => {
        roles.loading = true;
        try {
            const data = await store.dispatch('appRoles/listarRolesAction', page);
            roles.items = data.data || [];
            roles.page = data.current_page || page;
            roles.lastPage = data.last_page || 1;
            roles.total = data.total || 0;
        } catch (error) {
            showAlert(mensajeError(error, 'No se pudo cargar el listado de roles'), 'danger');
        } finally {
            roles.loading = false;
        }
    };

    const cargarTodosLosRoles = async () => {
        try {
            todosLosRoles.value = await store.dispatch('appRoles/listarRolesLigeroAction');
        } catch (e) {
            todosLosRoles.value = [];
        }
    };

    const cambiarPagina = (accion) => {
        let page = roles.page;
        if (accion === 'next') page += 1;
        else if (accion === 'previous') page -= 1;
        else page = Number(accion);
        if (page < 1 || page > roles.lastPage || page === roles.page) return;
        cargarRoles(page);
    };

    // El servidor no busca: se filtra la pagina actual
    const rolesFiltrados = computed(() => {
        const q = slugDe(busqueda.value);
        if (!q) return roles.items;
        return roles.items.filter((r) => slugDe(r.nombre).includes(q) || String(r.id) === busqueda.value.trim());
    });

    const resumenPermisos = (rol) => {
        const modulos = (rol.permissions || []).filter((p) => (p.actions || []).length);
        const totalAcciones = modulos.reduce((acc, p) => acc + p.actions.length, 0);
        return { modulos: modulos.length, acciones: totalAcciones };
    };

    // --- Crear / editar rol ------------------------------------------------
    const rolForm = reactive({
        active: false,
        id: null,
        nombre: '',
        descripcion: '',
        is_main: false,
        descripcionHtml: false,
        errores: {},
        error: null,
        guardando: false,
    });

    const abrirCrearRol = () => {
        Object.assign(rolForm, {
            active: true, id: null, nombre: '', descripcion: '', is_main: false,
            descripcionHtml: false, errores: {}, error: null, guardando: false,
        });
        cargarTodosLosRoles();
    };

    const abrirEditarRol = (rol) => {
        Object.assign(rolForm, {
            active: true,
            id: rol.id,
            nombre: rol.nombre || '',
            descripcion: textoPlano(rol.descripcion),
            is_main: !!Number(rol.is_main),
            descripcionHtml: textoPlano(rol.descripcion) !== (rol.descripcion || ''),
            errores: {},
            error: null,
            guardando: false,
        });
        cargarTodosLosRoles();
    };

    const cerrarRolForm = () => {
        if (rolForm.guardando) return;
        rolForm.active = false;
    };

    const nombreRepetido = computed(() => {
        const slug = slugDe(rolForm.nombre);
        if (!slug) return null;
        const lista = [...todosLosRoles.value, ...roles.items];
        return lista.find((r) => r.id !== rolForm.id && slugDe(r.nombre) === slug) || null;
    });

    const guardarRol = async () => {
        if (rolForm.guardando) return;
        rolForm.errores = {};
        rolForm.error = null;

        const nombre = rolForm.nombre.trim();
        if (!nombre) {
            rolForm.errores = { nombre: 'El nombre es obligatorio' };
            return;
        }
        if (!slugDe(nombre)) {
            rolForm.errores = { nombre: 'El nombre debe tener al menos una letra o número' };
            return;
        }

        // Nunca se manda `type`: con "public" el rol lo verian todas las inmobiliarias
        const payload = { nombre, descripcion: rolForm.descripcion.trim() };
        if (rolForm.id) payload.id = rolForm.id;
        if (esSuperAdmin.value) payload.is_main = rolForm.is_main;

        const creando = !rolForm.id;
        rolForm.guardando = true;
        try {
            const rol = await store.dispatch('appRoles/guardarRolAction', payload);
            rolForm.guardando = false;
            rolForm.active = false;

            if (creando) {
                showAlert(`Rol "${nombre}" creado. Ahora marca sus permisos.`);
                await cargarRoles(1);
                abrirMatriz(roles.items.find((r) => r.id === rol.id) || rol);
            } else {
                showAlert(`Rol "${nombre}" actualizado`);
                const fila = roles.items.find((r) => r.id === rol.id);
                if (fila) Object.assign(fila, { nombre: rol.nombre, slug: rol.slug, descripcion: rol.descripcion, is_main: rol.is_main });
                else cargarRoles();
            }
        } catch (error) {
            rolForm.error = mensajeError(error, 'No se pudo guardar el rol');
        } finally {
            rolForm.guardando = false;
        }
    };

    // --- Matriz de permisos ------------------------------------------------
    const matriz = reactive({
        active: false,
        rol: null,
        // { [moduloId]: { [accionId]: true } }
        marcadas: {},
        filtro: '',
        error: null,
        guardando: false,
    });

    const abrirMatriz = async (rol) => {
        const marcadas = {};
        (rol.permissions || []).forEach((p) => {
            marcadas[p.id] = {};
            (p.actions || []).forEach((a) => { marcadas[p.id][a.id] = true; });
        });
        Object.assign(matriz, { active: true, rol, marcadas, filtro: '', error: null, guardando: false });
        if (!catalogo.value.length) await cargarCatalogo();
    };

    const cerrarMatriz = () => {
        if (matriz.guardando) return;
        matriz.active = false;
    };

    const estaMarcada = (moduloId, accionId) => !!matriz.marcadas[moduloId]?.[accionId];

    const toggleAccion = (moduloId, accionId) => {
        if (!matriz.marcadas[moduloId]) matriz.marcadas[moduloId] = {};
        matriz.marcadas[moduloId][accionId] = !matriz.marcadas[moduloId][accionId];
    };

    const marcadasDeModulo = (modulo) => modulo.actions.filter((a) => estaMarcada(modulo.id, a.id)).length;

    // Si ya estan todas, las quita; si no, las marca todas
    const toggleModulo = (modulo) => {
        const todas = marcadasDeModulo(modulo) === modulo.actions.length;
        matriz.marcadas[modulo.id] = {};
        if (!todas) modulo.actions.forEach((a) => { matriz.marcadas[modulo.id][a.id] = true; });
    };

    const modulosMatriz = computed(() => {
        const q = slugDe(matriz.filtro);
        if (!q) return catalogo.value;
        return catalogo.value.filter((m) => slugDe(m.title).includes(q));
    });

    // Acciones activas que el catalogo ya no ofrece: al guardar se quitan
    const fueraDeCatalogo = computed(() => {
        if (!matriz.rol) return [];
        const resultado = [];
        (matriz.rol.permissions || []).forEach((p) => {
            const modulo = catalogo.value.find((m) => m.id === p.id);
            if (!modulo) return;
            (p.actions || []).forEach((a) => {
                if (!modulo.actions.some((x) => x.id === a.id)) {
                    resultado.push(`${modulo.title}: ${accionLabel(a.id, a.action)}`);
                }
            });
        });
        return resultado;
    });

    const totalMarcadas = computed(() =>
        catalogo.value.reduce((acc, m) => acc + marcadasDeModulo(m), 0)
    );

    const guardarMatriz = async () => {
        if (matriz.guardando || !matriz.rol) return;
        matriz.error = null;

        // Todo el catalogo, cada modulo con lo marcado (vacio = quitar todo).
        // Solo combinaciones del catalogo: el backend no lo valida.
        const permission = catalogo.value.map((m) => ({
            id: m.id,
            actions: m.actions.filter((a) => estaMarcada(m.id, a.id)).map((a) => ({ id: a.id })),
        }));

        matriz.guardando = true;
        try {
            const rol = await store.dispatch('appRoles/guardarMatrizAction', { role: matriz.rol.id, permission });
            const fila = roles.items.find((r) => r.id === matriz.rol.id);
            if (fila && rol.permissions) fila.permissions = rol.permissions;
            showAlert(`Permisos de "${matriz.rol.nombre}" guardados. Aplican cuando sus usuarios recarguen o vuelvan a iniciar sesión.`);
            matriz.guardando = false;
            matriz.active = false;
        } catch (error) {
            matriz.error = mensajeError(error, 'No se pudieron guardar los permisos');
        } finally {
            matriz.guardando = false;
        }
    };

    // --- Catalogo: modulos -------------------------------------------------
    const modulos = reactive({ items: [], loading: false, filtro: '', cargado: false });

    const cargarModulos = async () => {
        modulos.loading = true;
        try {
            modulos.items = await store.dispatch('appRoles/listarModulosAction');
            modulos.cargado = true;
        } catch (error) {
            showAlert(mensajeError(error, 'No se pudo cargar el listado de módulos'), 'danger');
        } finally {
            modulos.loading = false;
        }
    };

    const modulosFiltrados = computed(() => {
        const q = slugDe(modulos.filtro);
        if (!q) return modulos.items;
        return modulos.items.filter((m) => slugDe(m.title).includes(q) || slugDe(m.subject).includes(q));
    });

    // Lo que ofrece cada modulo sale de permissions-actions (solo los publicos)
    const accionesOfrecidas = (moduloId) => catalogo.value.find((m) => m.id === moduloId)?.actions || [];

    const moduloForm = reactive({ active: false, id: null, subject: '', title: '', errores: {}, error: null, guardando: false });

    const abrirModuloForm = (modulo = null) => {
        Object.assign(moduloForm, {
            active: true,
            id: modulo?.id || null,
            subject: modulo?.subject || '',
            title: modulo?.title || '',
            errores: {},
            error: null,
            guardando: false,
        });
    };

    const cerrarModuloForm = () => {
        if (moduloForm.guardando) return;
        moduloForm.active = false;
    };

    const guardarModulo = async () => {
        if (moduloForm.guardando) return;
        moduloForm.errores = {};
        moduloForm.error = null;

        const title = moduloForm.title.trim();
        const subject = moduloForm.subject.trim();
        const errores = {};
        if (!title) errores.title = 'El nombre es obligatorio';
        if (!moduloForm.id) {
            if (!subject) errores.subject = 'El subject es obligatorio';
            else if (!/^[a-z0-9_]+$/.test(subject)) errores.subject = 'Solo minúsculas, números y guion bajo';
            else if (modulos.items.some((m) => m.subject === subject)) errores.subject = 'Ya existe un módulo con ese subject';
        }
        if (Object.keys(errores).length) {
            moduloForm.errores = errores;
            return;
        }

        moduloForm.guardando = true;
        try {
            // El subject no se cambia al editar: lo usa el codigo del front (CASL)
            const payload = moduloForm.id ? { id: moduloForm.id, subject, title } : { subject, title };
            await store.dispatch('appRoles/guardarModuloAction', payload);
            showAlert(moduloForm.id ? `Módulo "${title}" actualizado` : `Módulo "${title}" creado. Define qué acciones ofrece.`);
            moduloForm.guardando = false;
            moduloForm.active = false;
            await Promise.all([cargarModulos(), cargarCatalogo()]);
        } catch (error) {
            moduloForm.error = mensajeError(error, 'No se pudo guardar el módulo');
        } finally {
            moduloForm.guardando = false;
        }
    };

    const ofrecidas = reactive({ active: false, modulo: null, seleccion: {}, error: null, guardando: false });

    const abrirOfrecidas = async (modulo) => {
        if (!acciones.value.length || !catalogo.value.length) await cargarCatalogo();
        const seleccion = {};
        accionesOfrecidas(modulo.id).forEach((a) => { seleccion[a.id] = true; });
        Object.assign(ofrecidas, { active: true, modulo, seleccion, error: null, guardando: false });
    };

    const cerrarOfrecidas = () => {
        if (ofrecidas.guardando) return;
        ofrecidas.active = false;
    };

    const idsOfrecidos = computed(() =>
        Object.keys(ofrecidas.seleccion).filter((id) => ofrecidas.seleccion[id]).map(Number)
    );

    const guardarOfrecidas = async () => {
        if (ofrecidas.guardando || !ofrecidas.modulo) return;
        ofrecidas.error = null;
        // Vacio deja el modulo sin acciones y el backend responde error
        if (!idsOfrecidos.value.length) {
            ofrecidas.error = 'Marca al menos una acción';
            return;
        }

        ofrecidas.guardando = true;
        try {
            await store.dispatch('appRoles/guardarAccionesModuloAction', {
                permission: ofrecidas.modulo.id,
                actions: idsOfrecidos.value,
            });
            showAlert(`Acciones de "${ofrecidas.modulo.title}" actualizadas`);
            ofrecidas.guardando = false;
            ofrecidas.active = false;
            await cargarCatalogo();
        } catch (error) {
            ofrecidas.error = mensajeError(error, 'No se pudieron guardar las acciones del módulo');
        } finally {
            ofrecidas.guardando = false;
        }
    };

    // --- Catalogo: acciones ------------------------------------------------
    const accionesFiltro = ref('');
    const accionesFiltradas = computed(() => {
        const q = slugDe(accionesFiltro.value);
        if (!q) return acciones.value;
        return acciones.value.filter((a) => slugDe(a.title).includes(q) || slugDe(a.action).includes(q));
    });

    const accionForm = reactive({ active: false, id: null, action: '', title: '', errores: {}, error: null, guardando: false });

    const abrirAccionForm = (accion = null) => {
        Object.assign(accionForm, {
            active: true,
            id: accion?.id || null,
            action: accion?.action || '',
            title: accion?.title || '',
            errores: {},
            error: null,
            guardando: false,
        });
    };

    const cerrarAccionForm = () => {
        if (accionForm.guardando) return;
        accionForm.active = false;
    };

    const guardarAccion = async () => {
        if (accionForm.guardando) return;
        accionForm.errores = {};
        accionForm.error = null;

        const title = accionForm.title.trim();
        const action = accionForm.action.trim();
        const errores = {};
        if (!title) errores.title = 'El nombre es obligatorio';
        if (!accionForm.id) {
            if (!action) errores.action = 'El código es obligatorio';
            else if (!/^[a-z0-9_]+$/.test(action)) errores.action = 'Solo minúsculas, números y guion bajo';
            else if (acciones.value.some((a) => a.action === action)) errores.action = 'Ya existe una acción con ese código';
        }
        if (Object.keys(errores).length) {
            accionForm.errores = errores;
            return;
        }

        accionForm.guardando = true;
        try {
            // El codigo no se cambia al editar: lo usa el codigo del front
            const payload = accionForm.id ? { id: accionForm.id, action, title } : { action, title };
            await store.dispatch('appRoles/guardarAccionAction', payload);
            showAlert(accionForm.id ? `Acción "${title}" actualizada` : `Acción "${title}" creada`);
            accionForm.guardando = false;
            accionForm.active = false;
            await store.dispatch('appRoles/getAccionesAction');
        } catch (error) {
            accionForm.error = mensajeError(error, 'No se pudo guardar la acción');
        } finally {
            accionForm.guardando = false;
        }
    };

    // --- Pestañas e inicio -------------------------------------------------
    const cambiarTab = (nuevo) => {
        tab.value = nuevo;
        if (nuevo === 'modulos' && !modulos.cargado) cargarModulos();
    };

    const iniciar = async () => {
        // Al recargar la pagina el userdata del login se pierde
        if (!usuario.value?.role) {
            await store.dispatch('appUserdata/getUserdata').catch(() => null);
        }
        await Promise.all([cargarRoles(1), cargarCatalogo()]);
    };

    return {
        alertTable,
        esSuperAdmin,
        tab,
        cambiarTab,
        iniciar,
        textoPlano,

        catalogo,
        acciones,
        loadingCatalogo,
        accionLabel,
        accionCodigo,
        tituloModulo,

        roles,
        busqueda,
        rolesFiltrados,
        cambiarPagina,
        resumenPermisos,

        rolForm,
        abrirCrearRol,
        abrirEditarRol,
        cerrarRolForm,
        nombreRepetido,
        guardarRol,

        matriz,
        abrirMatriz,
        cerrarMatriz,
        estaMarcada,
        toggleAccion,
        toggleModulo,
        marcadasDeModulo,
        modulosMatriz,
        fueraDeCatalogo,
        totalMarcadas,
        guardarMatriz,

        modulos,
        modulosFiltrados,
        accionesOfrecidas,
        moduloForm,
        abrirModuloForm,
        cerrarModuloForm,
        guardarModulo,
        ofrecidas,
        abrirOfrecidas,
        cerrarOfrecidas,
        idsOfrecidos,
        guardarOfrecidas,

        accionesFiltro,
        accionesFiltradas,
        accionForm,
        abrirAccionForm,
        cerrarAccionForm,
        guardarAccion,
    };
}
